import React, { useRef, useState } from "react"
import { Button, Form, Input, InputRef, Select, Tooltip } from "antd"
import {
  InformationCircleIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ewaAxiosInstance } from "utils/request"
import { useGet } from "utils/useFetch"
import { onQueryError } from "utils/utils"
import type { IShiftDifferentialsRes, IShiftPeriodsRes } from "../../typings"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import CustomInput from "./CustomInput"
import { WorkplaceName } from "./SetupDutyLocations"
import {
  ShiftPeriodCard,
  TimePicker,
} from "../../components/ShiftPeriodsAndAssignment"
import { toast } from "react-toastify"

// ─── Constants ────────────────────────────────────────────────────────────────

export const MERIDIEM_OPTIONS = [
  { value: "AM", label: "AM" },
  { value: "PM", label: "PM" },
]

export const SHIFT_DIFFERENTIAL_HINT =
  "A shift differential is extra pay for working this shift period — for example a night or hazard differential. Differentials are workplace-wide, and the percentage is set per paygrade, so the same differential can pay 40% for engineers and 20% for cleaners."

export const normalizeTimeInput = (value: string) =>
  value.replace(/\D/g, "").slice(0, 2)

// ─── Types ────────────────────────────────────────────────────────────────────

interface ShiftPeriodFormValues {
  periodName: string
  startHour: string
  startMinute: string
  startMeridiem: "am" | "pm"
  endHour: string
  endMinute: string
  endMeridiem: "am" | "pm"
  applyDifferentials: "yes" | "no"
  shiftDifferentialIds: number[]
}

interface CreateShiftPlanPeriodsProps {
  shiftPlanName?: string
  shiftPlanId?: number
  onShiftPeriodsChange?: (count: number) => void
}

interface ShiftPeriodApiBody {
  title: string
  startTime: string
  endTime: string
  shiftPlanId: string
  status: "active"
  shiftDifferentialIds: number[]
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function to24HourTime(hour: string, minute: string): string {
  let h = parseInt(hour || "12", 10)
  const m = parseInt(minute || "0", 10)
  if (Number.isNaN(h)) h = 12
  const mm = Number.isNaN(m) ? 0 : m
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`
}

function to24HourTimeFrom12Hour(
  hour: string,
  minute: string,
  meridiem: "am" | "pm"
): string {
  const h = Number(hour)
  const m = Number(minute)
  const safeHour = Number.isNaN(h) ? 12 : Math.min(12, Math.max(1, h))
  const safeMinute = Number.isNaN(m) ? 0 : Math.min(59, Math.max(0, m))
  const h24 =
    meridiem === "am"
      ? safeHour === 12
        ? 0
        : safeHour
      : safeHour === 12
      ? 12
      : safeHour + 12
  return `${String(h24).padStart(2, "0")}:${String(safeMinute).padStart(
    2,
    "0"
  )}`
}

function formatTimeFor24Hour(h: string, m: string) {
  return {
    hour: String(parseInt(h || "0", 10)).padStart(2, "0"),
    minute: String(parseInt(m || "0", 10)).padStart(2, "0"),
  }
}

function buildShiftPeriodPayload(
  values: ShiftPeriodFormValues,
  shiftPlanId: number
): ShiftPeriodApiBody {
  return {
    title: values.periodName,
    startTime: to24HourTimeFrom12Hour(
      values.startHour,
      values.startMinute,
      values.startMeridiem
    ),
    endTime: to24HourTimeFrom12Hour(
      values.endHour,
      values.endMinute,
      values.endMeridiem
    ),
    shiftPlanId: String(shiftPlanId),
    status: "active",
    shiftDifferentialIds:
      values.applyDifferentials === "yes"
        ? values.shiftDifferentialIds ?? []
        : [],
  }
}

// ─── Yes / No segmented toggle (controllable via antd Form.Item) ────────────────

export const YesNoToggle: React.FC<{
  value?: "yes" | "no"
  onChange?: (value: "yes" | "no") => void
}> = ({ value = "no", onChange }) => (
  <div className="inline-flex items-center gap-1 p-1 rounded-md border border-[#E2E8F0] bg-[#F8FAFC]">
    {(["yes", "no"] as const).map((option) => (
      <button
        key={option}
        type="button"
        onClick={() => onChange?.(option)}
        className={`px-3 py-1 rounded text-sm capitalize ${
          value === option
            ? "bg-white shadow-sm text-[#0F172B] font-medium"
            : "text-[#64748B]"
        }`}
      >
        {option}
      </button>
    ))}
  </div>
)

// ─── Component ────────────────────────────────────────────────────────────────

const CreateShiftPlanPeriods: React.FC<CreateShiftPlanPeriodsProps> = ({
  shiftPlanName = "January shift plan",
  shiftPlanId,
  onShiftPeriodsChange,
}) => {
  const [form] = Form.useForm<ShiftPeriodFormValues>()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingPeriodId, setEditingPeriodId] = useState<number | null>(null)
  const [deletingPeriodId, setDeletingPeriodId] = useState<number | null>(null)

  const queryClient = useQueryClient()

  const shiftPeriodsUrl = `/v1/time-attendance/shift-periods?page=1&limit=20&shiftPlanId=${
    shiftPlanId ?? ""
  }`

  // ── Query ──────────────────────────────────────────────────────────────────

  const { data: shiftPeriodsRes, isLoading: isShiftPeriodsLoading } =
    useGet<IShiftPeriodsRes>({
      url: shiftPeriodsUrl,
      instanceKey: "ewaAxiosInstance",
      enabled: !!shiftPlanId,
    })

  const docs = shiftPeriodsRes?.data?.docs ?? []

  const { data: shiftDifferentialsRes } = useGet<IShiftDifferentialsRes>({
    url: "/v1/time-attendance/shift-differentials?page=1&limit=100",
    instanceKey: "ewaAxiosInstance",
    enabled: modalOpen,
  })
  const shiftDifferentials = (shiftDifferentialsRes?.data?.docs ?? []).filter(
    (d) => d.isActive
  )
  const differentialOptions = shiftDifferentials.map((d) => ({
    label: d.name,
    value: d.id,
  }))
  const differentialTitleById = new Map(
    shiftDifferentials.map((d) => [d.id, d.name])
  )

  const applyDifferentials = Form.useWatch("applyDifferentials", form)
  const selectedDifferentialIds: number[] =
    Form.useWatch("shiftDifferentialIds", form) || []

  const periodCount = docs.length

  React.useEffect(() => {
    onShiftPeriodsChange?.(periodCount)
  }, [periodCount, onShiftPeriodsChange])

  // ── Mutations ──────────────────────────────────────────────────────────────

  const invalidatePeriods = () =>
    queryClient.invalidateQueries({ queryKey: [shiftPeriodsUrl] })

  const closeModal = () => {
    form.resetFields()
    setEditingPeriodId(null)
    setModalOpen(false)
  }

  const { mutate: createShiftPeriod, isLoading: isCreatingShiftPeriod } =
    useMutation({
      mutationFn: async (values: ShiftPeriodFormValues) => {
        if (!shiftPlanId) throw new Error("Missing shift plan id")
        const res = await ewaAxiosInstance.post(
          "/v1/time-attendance/shift-periods",
          buildShiftPeriodPayload(values, shiftPlanId)
        )
        return res.data
      },
      onSuccess: () => {
        invalidatePeriods()
        closeModal()
        toast.success("Shift Period Created")
      },
      onError: onQueryError,
    })

  const { mutate: updateShiftPeriod, isLoading: isUpdatingShiftPeriod } =
    useMutation({
      mutationFn: async ({
        id,
        values,
      }: {
        id: number
        values: ShiftPeriodFormValues
      }) => {
        if (!shiftPlanId) throw new Error("Missing shift plan id")
        const res = await ewaAxiosInstance.put(
          `/v1/time-attendance/shift-periods/${id}`,
          buildShiftPeriodPayload(values, shiftPlanId)
        )
        return res.data
      },
      onSuccess: () => {
        invalidatePeriods()
        closeModal()
        toast.success("Shift Period Updated Successfully")
      },
      onError: onQueryError,
    })

  const {
    mutate: deleteShiftPeriod,
    isLoading: isDeleting,
    variables,
  } = useMutation({
    mutationFn: async (id: number) => {
      const res = await ewaAxiosInstance.delete(
        `/v1/time-attendance/shift-periods/${id}`
      )
      return res.data
    },
    onMutate: setDeletingPeriodId,
    onSuccess: () => {
      invalidatePeriods()
      toast.success("Shift Period Deleted")
    },
    onError: onQueryError,
    onSettled: () => setDeletingPeriodId(null),
  })

  const isSaving = isCreatingShiftPeriod || isUpdatingShiftPeriod

  // ── Handlers ───────────────────────────────────────────────────────────────

  const openCreateModal = () => {
    form.resetFields()
    setEditingPeriodId(null)
    setModalOpen(true)
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      if (editingPeriodId != null) {
        updateShiftPeriod({ id: editingPeriodId, values })
      } else {
        createShiftPeriod(values)
      }
    } catch {
      // Validation errors are shown by Ant Design inline
    }
  }

  // ── Time picker refs ───────────────────────────────────────────────────────

  const startHourRef = useRef<InputRef>(null)
  const startMinuteRef = useRef<InputRef>(null)
  const endHourRef = useRef<InputRef>(null)
  const endMinuteRef = useRef<InputRef>(null)

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-[18px] text-[#0F172B]">
          Shift periods for <WorkplaceName name={shiftPlanName} />
        </h3>
        <Button
          type="default"
          className="font-semibold h-10 rounded-lg border-2 border-[#004AF5] text-[#004AF5] bg-white hover:!border-[#004AF5] hover:!text-[#004AF5] hover:!bg-[#F8FAFC] flex items-center gap-2"
          onClick={openCreateModal}
        >
          <PlusIcon className="w-4 h-4" />
          Add shift period
        </Button>
      </div>

      {isShiftPeriodsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004AF5]" />
        </div>
      ) : docs.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 py-12 px-6">
          <img
            src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/e11e623a-fe5a-40f5-b0ac-7dff66a27bad.svg"
            alt="No shift periods"
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
          {docs.map((period) => (
            <ShiftPeriodCard
              key={period.id}
              id={period.id}
              name={period.title}
              startTime={period.startTime}
              endTime={period.endTime}
              differentialCount={period.shiftDifferentials?.length ?? 0}
              onEdit={() => {
                const startRaw = period.startTime.split(":")
                const endRaw = period.endTime.split(":")
                const start = formatTimeFor24Hour(startRaw[0], startRaw[1])
                const end = formatTimeFor24Hour(endRaw[0], endRaw[1])
                const startHourNum = Number(start.hour)
                const endHourNum = Number(end.hour)
                const startMeridiem: "am" | "pm" =
                  startHourNum >= 12 ? "pm" : "am"
                const endMeridiem: "am" | "pm" = endHourNum >= 12 ? "pm" : "am"
                const startHour12 =
                  startHourNum % 12 === 0 ? 12 : startHourNum % 12
                const endHour12 = endHourNum % 12 === 0 ? 12 : endHourNum % 12
                const diffIds =
                  period.shiftDifferentials?.map((d) => d.id) ?? []
                form.setFieldsValue({
                  periodName: period.title,
                  startHour: String(startHour12).padStart(2, "0"),
                  startMinute: start.minute,
                  startMeridiem,
                  endHour: String(endHour12).padStart(2, "0"),
                  endMinute: end.minute,
                  endMeridiem,
                  applyDifferentials: diffIds.length ? "yes" : "no",
                  shiftDifferentialIds: diffIds,
                })
                setEditingPeriodId(period.id)
                setModalOpen(true)
              }}
              onDelete={() => deleteShiftPeriod(period.id)}
              isDeleting={isDeleting && variables === period.id}
            />
          ))}
          {docs.length % 2 === 1 && <div className="bg-white" />}
        </div>
      )}

      <CustomModal
        rootClassName="ta-scope"
        open={modalOpen}
        onCancel={closeModal}
        title={
          editingPeriodId != null ? "Edit shift period" : "Create shift period"
        }
        width={536}
        centered
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ applyDifferentials: "no", shiftDifferentialIds: [] }}
          onFinish={handleSave}
          className="[&_.ant-form-item]:!mb-0"
        >
          <div className="flex flex-col gap-5">
            <Form.Item
              name="periodName"
              label={
                <span className="font-bold text-sm text-[#0F172B]">
                  Shift period's name
                </span>
              }
              rules={[
                { required: true, message: "Please enter a shift period name" },
              ]}
            >
              <Input
                placeholder="Give this shift period a name e.g morning shift"
                className="h-10 rounded-[6px]"
              />
            </Form.Item>

            <div className="flex items-end gap-3">
              <TimePicker
                label="Start time"
                hourName="startHour"
                minuteName="startMinute"
                meridiemName="startMeridiem"
                hourRef={startHourRef}
                minuteRef={startMinuteRef}
                onHourComplete={() => startMinuteRef.current?.focus()}
                onMinuteBackspace={() => startHourRef.current?.focus()}
              />
              <span className="text-[#94A3B8] flex items-end pb-2.5">—</span>
              <TimePicker
                label="End time"
                hourName="endHour"
                minuteName="endMinute"
                meridiemName="endMeridiem"
                hourRef={endHourRef}
                minuteRef={endMinuteRef}
                onHourComplete={() => endMinuteRef.current?.focus()}
                onMinuteBackspace={() => endHourRef.current?.focus()}
              />
            </div>

            {/* Apply shift differentials */}
            <div className="flex items-center gap-3 pt-1 border-t border-[#E2E8F0]">
              <span className="flex items-center gap-1.5 font-bold text-sm text-[#0F172B] pt-4">
                Apply shift differentials
                <Tooltip title={SHIFT_DIFFERENTIAL_HINT}>
                  <span
                    tabIndex={0}
                    role="img"
                    aria-label={SHIFT_DIFFERENTIAL_HINT}
                    className="inline-flex text-[#64748B] cursor-help"
                  >
                    <InformationCircleIcon className="w-4 h-4" />
                  </span>
                </Tooltip>
              </span>
              <Form.Item
                name="applyDifferentials"
                className="!mb-0 pt-4"
              >
                <YesNoToggle />
              </Form.Item>
            </div>

            {applyDifferentials === "yes" && (
              <div className="flex flex-col gap-3 border-l-2 border-[#E2E8F0] pl-4">
                <span className="font-bold text-sm text-[#0F172B]">
                  Shift differentials
                </span>
                <Form.Item
                  name="shiftDifferentialIds"
                  className="!mb-0"
                >
                  <Select
                    mode="multiple"
                    placeholder="Select differentials to add"
                    options={differentialOptions}
                    className="w-full"
                    tagRender={() => <></>}
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
                {selectedDifferentialIds.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedDifferentialIds.map((id) => (
                      <span
                        key={id}
                        className="inline-flex items-center gap-2 rounded-md border border-[#E2E8F0] px-3 py-1.5 text-sm text-[#0F172B]"
                      >
                        {differentialTitleById.get(id) ?? id}
                        <button
                          type="button"
                          onClick={() =>
                            form.setFieldValue(
                              "shiftDifferentialIds",
                              selectedDifferentialIds.filter((x) => x !== id)
                            )
                          }
                        >
                          <XMarkIcon className="w-4 h-4 text-[#64748B]" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <Form.Item className="mt-5">
            <Button
              type="primary"
              className="w-full font-semibold h-11 rounded-lg bg-[#004AF5] text-white"
              onClick={() => form.submit()}
              loading={isSaving}
            >
              Save shift period
            </Button>
          </Form.Item>
        </Form>
      </CustomModal>
    </div>
  )
}

export default CreateShiftPlanPeriods
